<template>
  <div
    v-if="
      isPending || relatedArticles.length > 0 || relatedComparePages.length > 0
    "
    class="bg-surface-default-mid py-72"
  >
    <RevContainer>
      <template v-if="isPending">
        <RelatedContentSkeleton v-for="index in 2" :key="index" />
      </template>
      <div v-else class="space-y-48 text-left">
        <TrackedSection section-key="related_articles">
          <ArticleCardsCarousel
            :articles="relatedArticles"
            :title="i18n(translations.relatedArticlesTitle)"
            :tracking="articleCardTracking"
            @article-click="onCardClick"
          />
        </TrackedSection>
        <TrackedSection section-key="related_comparisons">
          <ArticleCardsCarousel
            :articles="relatedComparePages"
            :title="i18n(translations.relatedComparePagesTitle)"
            :tracking="comparisonCardTracking"
            @article-click="onCardClick"
          />
        </TrackedSection>
      </div>
    </RevContainer>
  </div>
</template>

<script lang="ts" setup>
import { useAsyncData, useRequestURL, useRoute } from '#imports'
import { computed, watch } from 'vue'

import ArticleCardsCarousel from '@backmarket/nuxt-layer-cms/ArticleCard/ArticleCardsCarousel.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevContainer } from '@ds/components/Container'

import { useCompareTrackingContext } from '../../../composables/useCompareTracking/useCompareTracking'
import { useFetchInternalLinkCards } from '../../../composables/useFetchInternalLinkCards/useFetchInternalLinkCards'
import TrackedSection from '../TrackedSection/TrackedSection.vue'

import translations from './RelatedContent.translations'
import RelatedContentSkeleton from './components/RelatedContentSkeleton.vue'

const i18n = useI18n()
const { pathname } = useRequestURL()
const fetchInternalLinkCards = useFetchInternalLinkCards()
const { data, pending: isPending } = useAsyncData(
  `models-comparison-related-content-${pathname}`,
  fetchInternalLinkCards,
)
const relatedArticles = computed(
  () => data.value?.comparePageRelatedArticles ?? [],
)
const relatedComparePages = computed(
  () => data.value?.comparePageRelatedComparePages ?? [],
)

// `Tracking` accepts `Record<string, string | number>`, so we filter out the
// `compare_category` key when it's undefined to keep types satisfied.
const compareContextRef = useCompareTrackingContext()
const compareContextEntries = computed<Record<string, string>>(() => {
  const ctx = compareContextRef.value
  if (!ctx) return {}

  return Object.fromEntries(
    Object.entries(ctx).filter(([, v]) => v !== undefined && v !== ''),
  )
})

const articleCardTracking = computed(() => ({
  ...compareContextEntries.value,
  name: 'article_card',
  zone: 'compare_articles_carousel',
}))
const comparisonCardTracking = computed(() => ({
  ...compareContextEntries.value,
  name: 'comparison_card',
  zone: 'compare_recent_carousel',
}))

// The page disables auto scroll-to-top (so the in-page switcher stays
// anchored), which also catches these carousel links — restore it manually.
const route = useRoute()
let shouldScrollOnNextRouteChange = false

function onCardClick() {
  shouldScrollOnNextRouteChange = true
}

watch(
  () => route.fullPath,
  () => {
    if (!shouldScrollOnNextRouteChange) return
    shouldScrollOnNextRouteChange = false
    window.scrollTo({ top: 0 })
  },
)
</script>
